import React, { memo, useState, useMemo } from "react";
import Box from "@mui/material/Box";

import DurationFilter, { IDurationOption } from "../../common/DurationFilter";
import RankTable from "./RankTable";

import { useAppSelector } from "../../../../store/hooks";
import useProductAnalyzerParams from "../../../../hooks/useProductAnalyzerParams";
import { selectProductAnalyzerSettings } from "../../../../features/settings/settings.selector";
import { selectRankAndPrice } from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { isNumber } from "../../../../helpers/formatter";

import { useStyles } from "./index.styles";
import { useMediaQuery } from "@mui/material";

const RankAndPrice: React.FC = () => {
  const { asin, lookupId } = useProductAnalyzerParams();
  const classes = useStyles();
  const { defaultRanksAndPricesTimeFrame, minBsr, maxBsr } = useAppSelector(
    selectProductAnalyzerSettings,
  );
  const [duration, setDuration] = useState<string>(
    defaultRanksAndPricesTimeFrame,
  );

  const { ranks, netBBPriceChange, categoryProductCount } = useAppSelector(
    (state) => selectRankAndPrice(state, duration),
  );

  const handleDurationChange = (option: IDurationOption) => {
    setDuration(option.id);
  };
  const isSmallScreen = useMediaQuery("(max-width:419px)");

  // Calculate health statuses
  const { pricingHealth, rankHealth } = useMemo(() => {
    const bsr: number = isNumber(ranks?.bsr) ? Number(ranks?.bsr) : -1;
    const bsrPercent: number =
      categoryProductCount > 0
        ? Math.ceil((bsr / categoryProductCount) * 100)
        : 0;
    const isCriteria = bsrPercent >= minBsr && bsrPercent <= maxBsr;

    const pricingHealthStatus =
      Math.abs(netBBPriceChange) <= 5 ? "STABLE" : "UNSTABLE";
    const rankHealthStatus = isCriteria ? "STABLE" : "UNSTABLE";

    return {
      pricingHealth: pricingHealthStatus,
      rankHealth: rankHealthStatus,
    };
  }, [ranks, netBBPriceChange, categoryProductCount, minBsr, maxBsr]);

  return (
    <Box className={classes.root}>
      <Box className={classes.filterBtnGroup}>
        <DurationFilter value={duration} onChange={handleDurationChange} />
      </Box>

      {/* Health Indicators */}
      <Box
        sx={{
          display: "flex",
          gap: "4px",
          py: "4px",
          px: "4px",
        }}
      >
        <Box
          sx={{
            flex: 1,
            borderRadius: "4px",
            backgroundColor: pricingHealth === "STABLE" ? "#DCFCE7" : "#F2DEDE",
            border: `1px solid ${
              pricingHealth === "STABLE" ? "#87E8AD" : "#A9444299"
            }`,
            color: pricingHealth === "STABLE" ? "#15803D" : "#A94442",
            px: "4px",
            py: "2px",
            fontSize: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "15px",
          }}
        >
          Pricing {isSmallScreen ? "" : "Health"}:&nbsp;
          <span style={{ fontWeight: 900 }}>{pricingHealth}</span>
        </Box>
        <Box
          sx={{
            flex: 1,
            borderRadius: "4px",
            backgroundColor: rankHealth === "STABLE" ? "#DCFCE7" : "#F2DEDE",
            border: `1px solid ${rankHealth === "STABLE" ? "#87E8AD" : "#A9444299"}`,
            color: rankHealth === "STABLE" ? "#15803D" : "#A94442",
            px: "12px",
            py: "4px",
            fontSize: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "15px",
          }}
        >
          Rank {isSmallScreen ? "" : "Health"}:&nbsp;
          <span style={{ fontWeight: 900 }}>{rankHealth}</span>
        </Box>
      </Box>

      <RankTable duration={duration} asin={asin} lookupId={lookupId} />
    </Box>
  );
};

export default memo(RankAndPrice);
